import type { CreateItemDto, ItemType } from '@/types/pricebook';

/**
 * The line fields a catalog item is built from. A structural subset of the
 * estimate's `EditableLineItem`, named here so this module does not depend on
 * the estimate route it is called from.
 */
export interface PromotableLine {
  name: string;
  code?: string;
  type: string;
  unit?: string | null;
  description?: string | null;
  basePrice?: number;
  costBasis?: number;
  markupPercent?: number;
  taxable?: boolean;
  taxLineType?: string | null;
  taxCategory?: string | null;
  taxCode?: string | null;
  glAccount?: string | null;
  glCogs?: string | null;
  glExpenseAccount?: string | null;
  glClass?: string | null;
  memberPrice?: number | null;
  memberDiscountPercent?: number | null;
  estimatedHours?: number;
  laborRate?: number;
  laborCostRate?: number;
  dynamicPricingConfig?: import('@/types/estimate').DocumentDynamicPricingConfig;
  laborCostLines?: import('@/types/pricebook').ServiceLaborLine[];
  priceStrategy?: string;
}

const CODE_PREFIX: Record<string, string> = {
  SERVICE: 'SVC',
  MATERIAL: 'MAT',
  EQUIPMENT: 'EQP',
  BUNDLE: 'BND',
  MEMBERSHIP: 'MEM',
};

const ITEM_TYPE_BY_LINE_TYPE: Record<string, ItemType> = {
  SERVICE: 'service',
  MATERIAL: 'material',
  EQUIPMENT: 'equipment',
  BUNDLE: 'bundle',
  MEMBERSHIP: 'membership',
};

/**
 * A catalog code derived from the line's name, in the shape the pricebook form
 * generates: `SVC-DRAIN-SNAKE`.
 *
 * The same slug rule as `nameToCode` on the pricebook create form, so a line
 * promoted here and an item typed there produce the same code for the same
 * name — an operator should not be able to tell which route an item came from.
 */
export function pricebookCodeForLine(name: string, type: string, attempt = 0): string {
  const prefix = CODE_PREFIX[type.trim().toUpperCase()] ?? 'ITM';
  const slug = name
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 20);
  const base = slug ? `${prefix}-${slug}` : prefix;
  // A tenant's codes are unique, and two lines called "Service call" are not a
  // conflict worth making the operator resolve by hand.
  return attempt > 0 ? `${base}-${attempt + 1}` : base;
}

/**
 * Why this line cannot become a catalog item yet, or null when it can.
 *
 * Checked here rather than left to the server so the operator is told what to
 * fix while the row is in front of them, instead of reading a 400 about a field
 * the estimate editor does not name.
 */
export function promotionBlockedReason(line: PromotableLine): string | null {
  if (!line.name?.trim()) return 'Give the line a name first.';
  if (!ITEM_TYPE_BY_LINE_TYPE[line.type?.trim().toUpperCase()]) {
    return 'This kind of line cannot be saved to the pricebook.';
  }
  return null;
}

/**
 * The catalog item a hand-typed line becomes.
 *
 * Everything the line can answer for itself is carried, because the point of
 * promoting it is that the next estimate should not have to be told again: its
 * price, what it costs, how it is taxed, where its money posts, what a member
 * pays, and — on a service — the labor behind the hours.
 *
 * What is deliberately NOT carried: the quantity, the optional/selected flags,
 * the chargeable flag and any per-document price override. Those describe THIS
 * sale, not the item; a catalog row that remembered them would re-apply one
 * document's decisions to every later one.
 */
/**
 * A dynamic configuration as the CATALOG may hold it.
 *
 * Drops `flatPricingBeforeDynamic` and keeps everything else. Written as an
 * explicit destructure rather than a field list so a configuration field added
 * later is carried by default — the parked pricing is the one thing that is
 * document-scoped, and naming only it means this cannot silently start dropping
 * a new field the item is priced by.
 */
function catalogPricingConfig(
  config: NonNullable<PromotableLine['dynamicPricingConfig']>,
): Record<string, unknown> {
  const { flatPricingBeforeDynamic: _documentOnly, ...catalogFields } = config as Record<
    string,
    unknown
  > & { flatPricingBeforeDynamic?: unknown };
  return catalogFields;
}

export function buildPricebookItemFromLine(
  line: PromotableLine,
  options: { codeAttempt?: number } = {},
): CreateItemDto {
  const normalizedType = line.type.trim().toUpperCase();
  const itemType = ITEM_TYPE_BY_LINE_TYPE[normalizedType] ?? 'service';
  const isService = itemType === 'service';
  // A service the operator switched to hourly. Its price is hours x rate, so
  // the rate — not `basePrice` — is what the item has to keep: on such a line
  // `basePrice` is whatever it held before the switch, often 0, and storing
  // that alone would create a catalog item priced at nothing.
  const isHourly = isService && String(line.priceStrategy ?? '').toUpperCase() === 'DYNAMIC';
  const carry = (value: string | null | undefined) => {
    const trimmed = value?.trim();
    return trimmed ? trimmed : undefined;
  };

  return {
    name: line.name.trim(),
    // A hand-typed line has no code — that is what made it custom.
    code: pricebookCodeForLine(line.name, normalizedType, options.codeAttempt ?? 0),
    description: line.description?.trim() || undefined,
    type: itemType,
    // The same rules the pricebook create form applies: an hourly service is
    // priced from its rate and measured in hours, so it carries no base price
    // and its unit is HR.
    basePrice: isHourly ? 0 : (line.basePrice ?? 0),
    unit: isHourly ? 'HR' : line.unit || 'each',
    priceStrategy: isHourly ? 'DYNAMIC' : 'FLAT',
    ...(isHourly && line.laborRate ? { laborRate: line.laborRate } : {}),
    // Carried when the line has one — a hand-typed line does not, but a line
    // that came from a dynamic catalog service and was then edited does, and
    // dropping it would flatten a configuration the item was priced by.
    //
    // WITHOUT the parked flat pricing. `flatPricingBeforeDynamic` records what
    // ONE line was selling for before its operator switched it to Dynamic; a
    // catalog item has no such history, and writing one in would hand the figure
    // to every future line made from this item as though the pricebook had
    // stated it. The document type declares the field and the catalog type
    // cannot (`DocumentDynamicPricingConfig` in types/estimate), but this spread
    // crosses that boundary at runtime, where a type filters nothing.
    ...(line.dynamicPricingConfig
      ? { dynamicPricingConfig: catalogPricingConfig(line.dynamicPricingConfig) }
      : {}),
    taxable: line.taxable ?? true,
    // Only a class the catalog can still express. `labor`/`materials` are
    // derived from the item's own type wherever it is sold, so storing one
    // would freeze today's derivation into the catalog row.
    ...(carry(line.taxLineType) === 'freight' ? { taxLineType: 'freight' as const } : {}),
    ...(carry(line.taxCategory) && { taxCategory: carry(line.taxCategory) }),
    ...(carry(line.taxCode) && { taxCode: carry(line.taxCode) }),
    ...(carry(line.glAccount) && { glAccount: carry(line.glAccount) }),
    ...(carry(line.glCogs) && { glCogs: carry(line.glCogs) }),
    ...(carry(line.glExpenseAccount) && { glExpenseAccount: carry(line.glExpenseAccount) }),
    ...(carry(line.glClass) && { glClass: carry(line.glClass) }),
    ...(line.memberPrice != null ? { memberPrice: line.memberPrice } : {}),
    ...(line.memberDiscountPercent != null
      ? { memberDiscountPercent: line.memberDiscountPercent }
      : {}),
    // Cost and markup describe a part. A service's cost is its labor, which
    // rides in the rows below rather than in a unit cost.
    ...(isService
      ? {}
      : { costBasis: line.costBasis ?? 0, markupPercent: line.markupPercent ?? 0 }),
    // A service's time: the rows the operator filled in, plus the reduced pair
    // costing actually reads. Written together so they cannot disagree.
    ...(isService && line.estimatedHours ? { estimatedHours: line.estimatedHours } : {}),
    ...(isService && line.laborCostRate ? { laborCostRate: line.laborCostRate } : {}),
    ...(isService && line.laborCostLines?.length ? { laborCostLines: line.laborCostLines } : {}),
  } as CreateItemDto;
}

/** Whether a failed create was the tenant's unique-code constraint. */
export function isDuplicateCodeError(error: unknown): boolean {
  const message = String(
    (error as { message?: unknown })?.message ??
      (error as { toString?: () => string })?.toString?.() ??
      '',
  ).toLowerCase();
  return message.includes('code') && (message.includes('exist') || message.includes('unique'));
}
